import { useTheme, useToastProvider } from "@deere/fuel-react";
import { Alert, Box, Typography } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import { useContext, useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { mapResponseToState } from "./apiUtils";
import D2DAcceptOrderModal from "./components/D2DAcceptOrderModal/D2DAcceptOrderModal";
import {
  D2DOrderRow,
  D2DStatus,
} from "./components/D2DActionMenu/D2DActionMenu";
import D2DRejectOrderModal from "./components/D2DRejectOrderModal/D2DRejectOrderModal";
import D2DRequestsTable from "./components/D2DRequestsTable/D2DRequestsTable";
import D2DShipOrderModal from "./components/D2DShipOrderModal/D2DShipOrderModal";
import {
  onFirstPageClick,
  onLastPageClick,
  onNextClick,
  onPreviousPageClick,
  onRowsPerPageChange,
} from "./paginationUtils";
import {
  mergeServerDataAction,
  setDataAction,
  setErrorAction,
  setFiltersAction,
  setLoadingAction,
  setPageAction,
  setPageRowSizeAction,
  updateRowAction,
} from "./reducers/actons";
import { d2dRequestsReducer, getInitialState } from "./reducers/reducers";
import {
  downloadD2DRequestsAsCSV,
  getTooltipTextForDownloadButton,
  isD2DRequestsFilterApplied,
} from "./utils/downloadUtils";
import d2dApi from "../../../api/d2d/d2dApi";
import Pagination from "../../../components/DataGridPro/Pagination/Pagination";
import ErrorPage from "../../../components/ErrorPage/ErrorPage";
import StickyFooter from "../../../components/StickyFooter/StickyFooter";
import { useConfigContext } from "../../../context/ConfigContext";
import { DealerAccountContext } from "../../../context/DealerAccountContext";
import { UserContext } from "../../../context/UserContext/UserContext";
import { getD2DRejectStatusLabelKey } from "../utils/rejectionStatus";

// Minimum total records required to display pagination
const PAGINATION_THRESHOLD = 10;
const SHIP_REFETCH_DELAY_MS = 600;

/**
 * Extract error message from API error response
 * API can return error in different formats:
 * - { detail: "Error message", status: 409, ... } - for conflict/error responses
 * - { message: "Error message" } - alternative format
 * - Standard error.message - for network errors
 */
const getErrorMessage = (error: any): string => {
  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return "An error occurred. Please try again.";
};

function D2DRequests() {
  const theme = useTheme();
  const { t } = useTranslation(["d2d", "common"]);
  const { authState } = useOktaAuth();
  const { D2D_INVENTORY_API_URL } = useConfigContext();
  const { dealerAccount } = useContext(DealerAccountContext);
  const user = useContext(UserContext);
  const { enqueueToast } = useToastProvider();

  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [selectedAcceptRequest, setSelectedAcceptRequest] =
    useState<D2DOrderRow | null>(null);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRejectRequest, setSelectedRejectRequest] =
    useState<D2DOrderRow | null>(null);

  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [selectedShipRequest, setSelectedShipRequest] =
    useState<D2DOrderRow | null>(null);

  const [successMessageKey, setSuccessMessageKey] = useState<string | null>(
    null
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const [state, dispatch] = useReducer(d2dRequestsReducer, undefined, () =>
    getInitialState()
  );

  // Tracks the highest total seen to avoid transient dips during refetch
  const [lastKnownTotal, setLastKnownTotal] = useState(0);

  const token = authState?.accessToken?.accessToken || "";
  const idToken = authState?.idToken?.idToken || "";
  const isAuthorized =
    !!token && !!dealerAccount?.id && !!D2D_INVENTORY_API_URL;

  // Decide which slice of rows to show for the current page.
  // If the API returns a full list, we slice by offset.
  // If the API already returns a single page (<= page size), use as-is.
  const getPageRows = (
    rows: D2DOrderRow[],
    currentPage: number,
    pageSize: number
  ) => {
    if (rows.length <= pageSize) return rows;
    const start = Math.max(0, (currentPage - 1) * pageSize);
    const end = start + pageSize;
    return rows.slice(start, end);
  };

  const applyRejectSubStatusFilter = (rows: D2DOrderRow[]): D2DOrderRow[] => {
    const statuses = state.filters.statuses ?? [];
    const wantsD2dClaimed = statuses.includes("D2D_CLAIMED" as any);
    const wantsManualReject = statuses.includes("MANUAL_REJECTED" as any);

    if (!wantsD2dClaimed && !wantsManualReject) {
      return rows;
    }

    return rows.filter((row) => {
      if (row.status !== "REJECTED") {
        return statuses.includes(row.status as any);
      }

      const rejectStatusKey = getD2DRejectStatusLabelKey({
        status: row.status,
        lastChangedBy: row.lastChangedBy,
        rejectionType: row.rejectionType,
        rejectionTrigger: row.rejectionTrigger,
      });

      return (
        (wantsD2dClaimed && rejectStatusKey === "d2d:status.d2dClaimed") ||
        (wantsManualReject && rejectStatusKey === "d2d:status.manualRejected")
      );
    });
  };

  /**
   * In-place update for a single row, preserving position and other rows.
   * Uses the reducer action to ensure state consistency.
   */
  const updateRow = (id: string, partial: Partial<D2DOrderRow>) => {
    dispatch(updateRowAction(id, partial));
  };

  /**
   * Silent background refetch to reconcile UI with server,
   * without toggling global loading state.
   * Uses MERGE_SERVER_DATA to preserve row positions after status changes.
   */
  const backgroundRefetch = async () => {
    if (!isAuthorized) return;
    try {
      const response = await d2dApi.fetchD2DRequests(
        D2D_INVENTORY_API_URL,
        token,
        dealerAccount!.id,
        state.currentPage,
        state.pageRowSize,
        state.filters
      );
      const { rows, totalCount } = mapResponseToState(response);
      const subFilteredRows = applyRejectSubStatusFilter(rows);
      const pageRows = getPageRows(
        subFilteredRows,
        state.currentPage,
        state.pageRowSize
      );
      // Use merge action to preserve row positions after status changes
      dispatch(mergeServerDataAction(pageRows, totalCount));
    } catch (e) {
      // keep UI state; optional: log
      console.error("Background refetch failed", e);
    }
  };

  // Accept flow
  const handleOpenAcceptModal = (row: D2DOrderRow) => {
    setSelectedAcceptRequest(row);
    setAcceptModalOpen(true);
  };

  const handleCloseAcceptModal = () => {
    setAcceptModalOpen(false);
    setSelectedAcceptRequest(null);
  };

  const handleConfirmAccept = async (
    request: D2DOrderRow,
    acceptedQuantity: number
  ) => {
    if (!isAuthorized) {
      handleCloseAcceptModal();
      return;
    }

    try {
      await d2dApi.acceptD2DOrder(D2D_INVENTORY_API_URL, token, idToken, {
        id: request.id,
        acceptedQty: acceptedQuantity,
        userName: user?.xId || "",
        action: "ACCEPT",
      });
      handleCloseAcceptModal();
      updateRow(request.id, {
        status: "ACCEPTED" as D2DStatus,
        acceptedQuantity,
      });
      handleAcceptSuccess();
      backgroundRefetch().catch(() => undefined);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      enqueueToast({
        variant: "error",
        message: errorMessage,
        isCloseable: true,
      });
      setSuccessMessageKey(null);
      handleCloseAcceptModal();
    }
  };

  const handleAcceptSuccess = () => {
    setSuccessMessageKey("d2dRequests.success.accepted");
  };

  // Reject flow
  const handleOpenRejectModal = (row: D2DOrderRow) => {
    setSelectedRejectRequest(row);
    setRejectModalOpen(true);
  };

  const handleCloseRejectModal = () => {
    setRejectModalOpen(false);
    setSelectedRejectRequest(null);
  };

  const handleConfirmReject = async (row: D2DOrderRow) => {
    if (!isAuthorized) {
      handleCloseRejectModal();
      return;
    }

    try {
      await d2dApi.rejectD2DOrder(D2D_INVENTORY_API_URL, token, idToken, {
        id: row.id,
        userName: user?.xId || "",
        action: "REJECT",
      });
      handleCloseRejectModal();
      updateRow(row.id, { status: "REJECTED" as D2DStatus });
      handleRejectSuccess();
      backgroundRefetch().catch(() => undefined);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      enqueueToast({
        variant: "error",
        message: errorMessage,
        isCloseable: true,
      });
      setSuccessMessageKey(null);
      handleCloseRejectModal();
    }
  };

  const handleRejectSuccess = () => {
    setSuccessMessageKey("d2dRequests.success.rejected");
  };

  // Ship flow
  const handleOpenShipModal = (row: D2DOrderRow) => {
    setSelectedShipRequest(row);
    setShipModalOpen(true);
  };

  const handleCloseShipModal = () => {
    setShipModalOpen(false);
    setSelectedShipRequest(null);
  };

  const handleConfirmShip = async (
    row: D2DOrderRow,
    carrierName: string,
    trackingNumber: string
  ) => {
    if (!isAuthorized) {
      handleCloseShipModal();
      return;
    }

    try {
      updateRow(row.id, {
        status: "SHIPPED" as D2DStatus,
        carrierName,
        trackingNumber,
      });
      await d2dApi.shipD2DOrder(D2D_INVENTORY_API_URL, token, idToken, {
        id: row.id,
        carrierName,
        trackingNumber,
        userName:
          user?.xId || (authState?.accessToken?.claims?.userID as string) || "",
        action: "MARKASSHIPPED",
        acceptedQty: row.acceptedQuantity || row.requestedQuantity || 0,
      });
      handleCloseShipModal();
      handleShipSuccess();
      setTimeout(() => {
        backgroundRefetch().catch(() => undefined);
      }, SHIP_REFETCH_DELAY_MS);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      enqueueToast({
        variant: "error",
        message: errorMessage,
        isCloseable: true,
      });
      setSuccessMessageKey(null);
      updateRow(row.id, { status: row.status });
      handleCloseShipModal();
    }
  };

  const handleCloseSuccessBanner = () => {
    setSuccessMessageKey(null);
  };

  const handleShipSuccess = () => {
    setSuccessMessageKey("d2dRequests.success.markAsShipped");
  };

  const handleViewOrderSummaryFromMenu = (row: D2DOrderRow) => {
    setSelectedShipRequest(row);
    setShipModalOpen(true);
  };

  const handleDownload = () => {
    setIsDownloading(true);
    try {
      downloadD2DRequestsAsCSV(state.requests || [], t);
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Primary fetch driven by page/size/search changes.
   */
  const fetchRequests = async () => {
    if (!isAuthorized) {
      return;
    }

    try {
      dispatch(setLoadingAction(true));
      dispatch(setErrorAction(false));
      const response = await d2dApi.fetchD2DRequests(
        D2D_INVENTORY_API_URL,
        token,
        dealerAccount!.id,
        state.currentPage,
        state.pageRowSize,
        state.filters
      );
      const { rows, totalCount } = mapResponseToState(response);
      const subFilteredRows = applyRejectSubStatusFilter(rows);
      const pageRows = getPageRows(
        subFilteredRows,
        state.currentPage,
        state.pageRowSize
      );

      dispatch(setDataAction(pageRows, totalCount));
    } catch (e) {
      console.error("Failed to fetch D2D requests", e);
      dispatch(setErrorAction(true));
    } finally {
      dispatch(setLoadingAction(false));
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [
    isAuthorized,
    D2D_INVENTORY_API_URL,
    dealerAccount?.id,
    state.currentPage,
    state.pageRowSize,
    state.filters,
  ]);

  // Update last-known total with the highest positive value seen so far
  // to prevent pagination total flicker during refetch.
  useEffect(() => {
    if (!state.isLoading && state.totalRequests > 0) {
      setLastKnownTotal((prev) => Math.max(prev, state.totalRequests));
    }
  }, [state.isLoading, state.totalRequests]);

  // Reset last-known total when search or account context changes
  useEffect(() => {
    setLastKnownTotal(0);
  }, [dealerAccount?.id, state.filters]);

  // Pagination and totals

  if (!isAuthorized) {
    return (
      <Box sx={{ display: "flex", height: "70vh", justifyContent: "center" }}>
        <ErrorPage title="Error" />
      </Box>
    );
  }

  const rowsPerPage = Math.max(1, state.pageRowSize);

  // Use the max of current and last-known totals to avoid transient dips
  const effectiveTotal = Math.max(lastKnownTotal, state.totalRequests);

  // Show pagination only when API total meets threshold
  const showPagination = state.totalRequests >= PAGINATION_THRESHOLD;

  // Normalize pagination model for DataGridPro
  const paginationModel = {
    page: Math.max(0, (state.currentPage || 1) - 1),
    pageSize: rowsPerPage,
  } as const;

  return (
    <>
      <Typography
        component="h1"
        data-fuel-size="3xl"
        fontWeight="semibold"
        sx={{
          paddingBottom: theme.tokens.FuelSpacing6,
          color: theme.tokens.FuelColorTextPrimary,
        }}
        data-testid="d2d-requests-title"
      >
        {t("d2d:d2dRequests.header.title")}
      </Typography>

      {successMessageKey && (
        <Box sx={{ p: theme.tokens.FuelSpacing6 }}>
          <Alert
            severity="success"
            onClose={handleCloseSuccessBanner}
            sx={{
              mb: theme.tokens.FuelSpacing4,
              backgroundColor: "#2e7d32",
              color: "#ffffff",
              borderRadius: 0,
              boxShadow: "none",
              alignItems: "center",
              "& .MuiAlert-icon": {
                color: "#ffffff",
              },
              "& .MuiSvgIcon-root": {
                fontSize: "1.5rem",
              },
              "& .MuiAlert-action": {
                color: "#ffffff",
              },
            }}
            data-testid="d2d-success-banner"
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "inherit" }}
            >
              {t(`d2d:${successMessageKey}`)}
            </Typography>
          </Alert>
        </Box>
      )}

      <D2DRequestsTable
        rows={state.requests || []}
        loading={state.isLoading}
        onViewOrderSummary={handleViewOrderSummaryFromMenu}
        onOpenAcceptModal={handleOpenAcceptModal}
        onOpenRejectModal={handleOpenRejectModal}
        onOpenShipModal={handleOpenShipModal}
        filters={state.filters}
        onFiltersChange={(filters) => dispatch(setFiltersAction(filters))}
        onClearFilters={() => dispatch(setFiltersAction({}))}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          dispatch(setPageRowSizeAction(model.pageSize));
          dispatch(setPageAction(model.page + 1));
        }}
        rowCount={effectiveTotal}
        download={{
          isDownloading,
          onDownload: handleDownload,
          tooltipText: getTooltipTextForDownloadButton(
            state.requests || [],
            isD2DRequestsFilterApplied(state.filters),
            isDownloading,
            t
          ),
        }}
      />

      {showPagination && (
        <StickyFooter
          sx={{ justifyContent: "flex-start", zIndex: 1000, marginBottom: 0 }}
        >
          <Pagination
            rowsPerPage={rowsPerPage}
            currentPage={state.currentPage}
            totalCount={effectiveTotal}
            onRowsPerPageChange={(updated) =>
              onRowsPerPageChange(dispatch, updated)
            }
            onFirstPageClick={() => onFirstPageClick(dispatch)}
            onPreviousClick={() =>
              onPreviousPageClick(dispatch, state.currentPage)
            }
            onLastPageClick={() =>
              onLastPageClick(dispatch, rowsPerPage, effectiveTotal)
            }
            onNextClick={() => onNextClick(dispatch, state.currentPage)}
          />
        </StickyFooter>
      )}

      <D2DAcceptOrderModal
        open={acceptModalOpen}
        request={selectedAcceptRequest}
        onClose={handleCloseAcceptModal}
        onAccept={handleConfirmAccept}
      />

      <D2DRejectOrderModal
        open={rejectModalOpen}
        request={selectedRejectRequest}
        onClose={handleCloseRejectModal}
        onReject={handleConfirmReject}
      />

      <D2DShipOrderModal
        open={shipModalOpen}
        request={selectedShipRequest}
        onClose={handleCloseShipModal}
        onMarkShipped={handleConfirmShip}
      />
    </>
  );
}

export default D2DRequests;
